/**
 * 「最後に使ったログイン方法」をこの端末に覚える（2026-09-28 creator/login-redesign）。
 *
 * WHY: founder が Vercel / Cloudflare のログイン画面を参考に送ってきた（docs/teams/creator/tasks/login-redesign.md）。
 *   GitHub で入った人が次回もまず GitHub のボタンを探す・メールの人が毎回メールアドレスを打つ、の手間を減らす。
 *   画面（pages/AuthPage.tsx）は「前回の方法を先頭に・小さな印（前回使用 / Last used）」を出し、メールの人は欄に入れておく。
 *
 * 保存するもの（**これだけ**）:
 *   - 方法の名前（email / google / apple / github / passkey）
 *   - メールでログインした人のメールアドレス（欄に入れておくため）
 *   - 「保存しない」を選んだ印（remember: false）
 * 保存しないもの: パスワード・トークン・プロバイダのアカウント名。パスワードはブラウザ / パスワード管理が持つ（authForms.ts）。
 *
 * WHY localStorage か: 「この端末で次回」のための値なのでタブを閉じても残す必要がある。sessionStorage では次回に残らない。
 *   読めない（Safari のプライベート・ストレージ禁止）・壊れた値のときは「何も覚えていない」として動く（try/catch・検査）。
 * WHY 「保存しない」を覚えるか: 共有の端末でチェックを外した人の次に来た人が、既定 ON のまま気づかず保存しないように、
 *   外した選択そのものは残す（個人の情報は含まない。外した時点で方法とメールは消す = 指示書 3）。
 *
 * 判断はこのファイル（node --test で確かめる: lastLogin.test.ts）。ストレージは引数で受ける（テストでは偽物を渡す）。
 */
import { DEV_OAUTH_PROVIDERS, type DevOAuthProvider } from './devOAuth.ts';

/** ログインの方法。passkey = ログイン用パスキー（2026-09-28 creator/passkey-login から記録する: components/OAuthButtons.tsx が成功時に recordLogin） */
export type LoginMethod = 'email' | 'passkey' | DevOAuthProvider;
const METHODS: readonly string[] = ['email', 'passkey', ...DEV_OAUTH_PROVIDERS];
export const isLoginMethod = (v: unknown): v is LoginMethod => typeof v === 'string' && METHODS.includes(v);

/** localStorage のキー。v1 = 形を変えたら v2 にして古い値は読まない（壊れた値で画面を止めない） */
export const LAST_LOGIN_KEY = 'kumo.lastLogin.v1';

export type LastLogin =
  | { remember: true; method: LoginMethod; email?: string }
  // remember: false = 利用者が「保存する」を外した。方法もメールも持たない
  | { remember: false };

/** Storage のうち使う分だけ（テストの偽物を渡せるように） */
export type KvStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** 画面から使う localStorage（読めない環境では null = 何も覚えない） */
export function deviceStorage(): KvStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

// メールの欄と同じ上限（authForms.ts の emailFieldProps の maxLength・サーバーの email.max(254)）。
// それより長い値は自分が書いたものではない = 捨てる
const MAX_EMAIL = 254;

/** 覚えている内容。無い・読めない・形が違うときは null（= 初めての端末と同じ見た目） */
export function readLastLogin(storage: KvStorage | null): LastLogin | null {
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(LAST_LOGIN_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { remember?: unknown; method?: unknown; email?: unknown };
    if (v.remember === false) return { remember: false };
    if (v.remember !== true || !isLoginMethod(v.method)) return null;
    // メールアドレスは「メールでログインした」ときだけ持つ（ほかの方法のときに残っていても使わない）
    const email = v.method === 'email' && typeof v.email === 'string' && v.email.length <= MAX_EMAIL && v.email.includes('@') ? v.email : undefined;
    return email ? { remember: true, method: v.method, email } : { remember: true, method: v.method };
  } catch {
    return null;
  }
}

/** チェックボックスの既定: 何も無い = ON（指示書 3）。外した記録があれば OFF のまま出す */
export function rememberDefault(saved: LastLogin | null): boolean {
  return saved?.remember !== false;
}

function write(storage: KvStorage | null, v: LastLogin): void {
  if (!storage) return;
  try {
    storage.setItem(LAST_LOGIN_KEY, JSON.stringify(v));
  } catch {
    // 容量超過・禁止 = 覚えられないだけ。ログインそのものは止めない
  }
}

/**
 * チェックボックスを切り替えたとき（ログインの前でも）。
 *   OFF → 保存済みの方法・メールをすぐ消し、「保存しない」だけを残す
 *   ON  → 「保存しない」の印を消す（次にログインが成功した時点で方法を書く）
 * WHY 切り替えた瞬間に消すか: 共有の端末で「外したのにメールが欄に残っている」を見せない。送信を待たない
 */
export function setRemember(storage: KvStorage | null, remember: boolean): void {
  if (!storage) return;
  if (!remember) {
    write(storage, { remember: false });
    return;
  }
  const cur = readLastLogin(storage);
  if (cur?.remember === false) {
    try {
      storage.removeItem(LAST_LOGIN_KEY);
    } catch {
      /* 同上 */
    }
  }
}

/**
 * ログインが成功したときに呼ぶ（メール・パスワード = AuthPage、Google 等 = OAuthCallback）。
 * 「保存しない」を選んでいる端末では何も書かない。メールアドレスは method=email のときだけ保存する
 * （Google 等で入ったときは前のメールも消す = 覚える量を最小にする）。
 */
export function recordLogin(storage: KvStorage | null, method: LoginMethod, email?: string): void {
  const cur = readLastLogin(storage);
  if (cur?.remember === false) return;
  const e = method === 'email' ? email?.trim() : undefined;
  write(storage, e && e.length <= MAX_EMAIL ? { remember: true, method, email: e } : { remember: true, method });
}

/** 前回使った方法（印と並び順に使う）。保存しない・何も無いときは null */
export function lastMethod(saved: LastLogin | null): LoginMethod | null {
  return saved?.remember ? saved.method : null;
}

/** 外部のボタンの並び: 前回のプロバイダを先頭へ。ほかはサーバーの順のまま（ボタンの種類は増やさない・減らさない） */
export function orderProviders<T extends string>(providers: readonly T[], last: LoginMethod | null): T[] {
  const i = last ? providers.indexOf(last as T) : -1;
  if (i <= 0) return [...providers];
  return [providers[i] as T, ...providers.slice(0, i), ...providers.slice(i + 1)];
}
